import { API_URL } from '../config/api';
import { getAuthToken as getAuthTokenUtil } from '../utils/authToken';

export interface MemberPermissions {
  isMember: boolean;
  isAdmin: boolean;
  organizationId: string | null;
  organizationTier: string | null; // Inherited tier from organization
  role: 'admin' | 'member' | 'viewer' | 'none';
  blockedElements: string[]; // Elements blocked by admin policy
}

/**
 * Service to check member permissions and organization access
 */
export class MemberPermissionsService {
  private static cache: Map<string, { data: MemberPermissions; timestamp: number }> = new Map();
  private static CACHE_TTL = 60000; // 1 minute cache

  /**
   * Get member permissions for current user
   */
  static async getMemberPermissions(
    userId: string | null | undefined,
    getToken: (() => Promise<string | null>) | null | undefined
  ): Promise<MemberPermissions> {
    if (!userId || !getToken) {
      return {
        isMember: false,
        isAdmin: false,
        organizationId: null,
        organizationTier: null,
        role: 'none',
        blockedElements: []
      };
    }

    // Check cache first (cache key includes userId to differentiate impersonated users)
    const cached = this.cache.get(userId);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      console.log('🔍 MemberPermissionsService - Using cached permissions for user:', userId);
      return cached.data;
    }
    
    console.log('🔍 MemberPermissionsService - Fetching fresh permissions for user:', userId);

    try {
      // Use impersonation-aware token (checks impersonation first, then Clerk)
      // This ensures when admin impersonates, we get permissions for impersonated user
      const token = getToken ? await getToken() : await getAuthTokenUtil();
      if (!token) {
        return {
          isMember: false,
          isAdmin: false,
          organizationId: null,
          organizationTier: null,
          role: 'none',
          blockedElements: []
        };
      }

      // Fetch member permissions from backend
      // Backend will use impersonation token to get permissions for impersonated user
      console.log('🔍 MemberPermissionsService - Fetching permissions with token (first 20 chars):', token.substring(0, 20));
      const response = await fetch(`${API_URL}/api/members/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ MemberPermissionsService - Failed to fetch member permissions:', response.status, errorText);
        return {
          isMember: false,
          isAdmin: false,
          organizationId: null,
          organizationTier: null,
          role: 'none',
          blockedElements: []
        };
      }

      const data = await response.json();
      console.log('🔍 MemberPermissionsService - Received data from backend:', data);
      
      if (!data.success) {
        console.warn('⚠️ MemberPermissionsService - Backend returned success=false:', data);
        return {
          isMember: false,
          isAdmin: false,
          organizationId: null,
          organizationTier: null,
          role: 'none',
          blockedElements: []
        };
      }

      const result: MemberPermissions = {
        isMember: data.isMember || false,
        isAdmin: data.isAdmin || false,
        organizationId: data.organizationId || null,
        organizationTier: data.organizationTier || null,
        role: data.role || 'none',
        blockedElements: data.blockedElements || []
      };

      // Cache with userId as key (different for each impersonated user)
      this.cache.set(userId, { data: result, timestamp: Date.now() });
      console.log('✅ MemberPermissionsService - Cached permissions for user:', userId, {
        isMember: result.isMember,
        isAdmin: result.isAdmin,
        organizationTier: result.organizationTier,
        role: result.role,
        blockedCount: result.blockedElements.length
      });
      return result;
    } catch (error) {
      console.error('Error fetching member permissions:', error);
      return {
        isMember: false,
        isAdmin: false,
        organizationId: null,
        organizationTier: null,
        role: 'none',
        blockedElements: []
      };
    }
  }

  /**
   * Clear cache for a user
   */
  static clearCache(userId?: string): void {
    if (userId) {
      this.cache.delete(userId);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Check if an addon is blocked for the user
   */
  static isAddonBlocked(addonId: string, blockedElements: string[]): boolean {
    // Map addon IDs to element IDs
    const addonToElementMap: Record<string, string> = {
      'onlineScheduler': 'onlineScheduler',
      'customButton': 'customButton',
      'paymentButton': 'paymentButton',
      'leave_feedback': 'leave_feedback',
      'promotion_button': 'promotion_button',
      'video_conference': 'video_conference',
      'webinar': 'webinar',
      'app_download': 'app_download',
      'join_newsletter': 'join_newsletter',
      'image_gallery': 'image_gallery',
      'quotes': 'quotes',
      'digital_card_save': 'digital_card_save',
      'digital_card_view': 'digital_card_view',
      'digital_card_qr': 'digital_card_qr',
      'social_links': 'social_links',
      'disclaimer': 'disclaimer',
      'banner': 'banner',
      'banner_house': 'banner_house',
      'green_footer': 'green_footer',
      'youtube_status': 'youtube_status'
    };

    const elementId = addonToElementMap[addonId] || addonId;
    return blockedElements.includes(elementId);
  }

  /**
   * Get template limit for member (1 template)
   */
  static getMemberTemplateLimit(role: 'admin' | 'member' | 'viewer' | 'none'): number {
    if (role === 'member') {
      return 1; // Members limited to 1 template
    }
    // Admins have no limit (or use organization tier limit)
    return -1; // -1 means unlimited (or use tier-based limit)
  }
}

